// @flow
import { FormattedMessage } from 'react-intl';
import {
  DO_NOT_WISH_PROVIDE_OPTION,
  ethnicityOptions,
  genderOptions,
  raceOptions,
  // eslint-disable-next-line max-len
} from '../../../components/LeaseAffordableApplicantFormCommonSections/configuration';
import { EMAIL_TYPES, PHONE_TYPES } from '../../../constants/contactTypes';
import { isValidDate } from '../utils/validation';
import messages from '../messages';

// A field carrying both `required` and `hidden` is asked for only where the
// configuration renders it: the library drops a hidden field's rule and
// unregisters it. That is what scopes the demographics to the waitlist field
// set without a second list naming them. Contact Information is sliced the
// same way on the conventional field set; the backend then keeps the
// prospect's own phone and email.
// Every field takes half a row, so the section reads as two columns; only the
// contact subheading spans both.
export const buildPrimaryApplicantFields = ({
  intl,
  showSubmissionStamp,
  canEditSubmissionDateTime = true,
  onSubmissionDateTimeBlur,
  onSubmissionDateTimeAccept,
  showDemographics,
  showMailingAddress,
  showContactInformation,
  propertyStateCode = '',
  suffixOptions = [],
  isSuffixesLoading = false,
  // The same lists the lease application and household forms offer.
  phoneTypeOptions = PHONE_TYPES,
  emailTypeOptions = EMAIL_TYPES,
  stateOptions = [],
  isStatesLoading = false,
}: {
  intl: Object,
  showSubmissionStamp: boolean,
  canEditSubmissionDateTime?: boolean,
  onSubmissionDateTimeBlur?: () => void,
  onSubmissionDateTimeAccept?: (value: mixed) => void,
  showDemographics: boolean,
  showMailingAddress: boolean,
  showContactInformation: boolean,
  propertyStateCode?: string,
  suffixOptions?: Array<Object>,
  isSuffixesLoading?: boolean,
  phoneTypeOptions?: Array<Object>,
  emailTypeOptions?: Array<Object>,
  stateOptions?: Array<Object>,
  isStatesLoading?: boolean,
}): Array<Object> => [
  {
    variant: 'dateTime',
    name: 'submissionDateTime',
    label: intl.formatMessage(messages.submissionDateTime),
    hidden: !showSubmissionStamp,
    // Program availability follows the date only once the agent leaves the
    // field, so a date passing through other values as it is typed never
    // unticks a program on the way. A calendar pick never blurs the input —
    // focus returns to the picker button — so the picker's accept is the other
    // way out. Typing never fires it.
    onBlur: onSubmissionDateTimeBlur,
    onAccept: onSubmissionDateTimeAccept,
    // The record cannot hold an empty stamp — the edit route refuses a null —
    // so clearing the field is refused here rather than saved as a no-op. Only
    // asked of an agent who can actually set it: a locked field the record left
    // empty would otherwise block the save with no way to unblock it.
    ...(canEditSubmissionDateTime
      ? {
          rules: {
            required: true,
            // `required` only catches an empty field. Clearing or half-typing
            // the date leaves an Invalid Date, which is truthy and would sail
            // through to the route as a null.
            //
            // Returned as a message rather than as `false`: the form only
            // paints a field red where the error carries one, and `required`
            // is the only rule the library writes a default message for.
            validate: (value) =>
              !value ||
              isValidDate(value) ||
              intl.formatMessage(messages.invalidSubmissionDateTime),
          },
        }
      : {}),
    // Editing the stamp is its own permission, and the refusal is spelled out
    // rather than left to be guessed at. Two quirks of the dateTime field decide
    // how: it builds its own `slotProps.textField` and forwards only a fixed set
    // of props to it, so the InputLabelProps tooltip the date field takes never
    // arrives; and it blanks the helper text whenever `readOnly` is set, so the
    // field is disabled instead — which is what stops the picker anyway.
    ...(canEditSubmissionDateTime
      ? {}
      : {
          disabled: true,
          helperText: (
            <FormattedMessage
              {...messages.editSubmissionDateTimeNoPermissionTooltip}
            />
          ),
        }),
    GridProps: { xs: 12, sm: 6 },
  },
  {
    // Keeps the stamp on a row of its own, so the name pair below it starts one,
    // and so leaves with it rather than holding a gap open.
    variant: 'space',
    hidden: !showSubmissionStamp,
    GridProps: { xs: 12, sm: 6, sx: { display: { xs: 'none', sm: 'block' } } },
  },
  {
    variant: 'text',
    name: 'firstName',
    label: intl.formatMessage(messages.firstName),
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'middleName',
    label: intl.formatMessage(messages.middleName),
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'lastName',
    label: intl.formatMessage(messages.lastName),
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'suffix',
    label: intl.formatMessage(messages.suffix),
    options: suffixOptions,
    disabled: isSuffixesLoading,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'date',
    name: 'dateOfBirth',
    label: intl.formatMessage(messages.dateOfBirth),
    rules: { required: true },
    hidden: !showDemographics,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'gender',
    label: intl.formatMessage(messages.gender),
    options: genderOptions,
    rules: { required: true },
    hidden: !showDemographics,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    // A household may report more than one race, and the affordable state
    // reports read every entry.
    variant: 'multiSelect',
    name: 'multiRace',
    label: intl.formatMessage(messages.race),
    // "Do not wish to provide" answers the question instead of narrowing it, so
    // it is handed over as the no-preference option — the select then collapses
    // the selection to it alone and disables the rest — and taken out of the
    // list it would otherwise appear in twice.
    options: raceOptions(propertyStateCode, { withDefault: false }).filter(
      ({ value }) => value !== DO_NOT_WISH_PROVIDE_OPTION.value,
    ),
    noPreferenceOptionEnabled: true,
    noPreferenceOption: DO_NOT_WISH_PROVIDE_OPTION,
    rules: { required: true },
    hidden: !showDemographics,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'ethnicity',
    label: intl.formatMessage(messages.ethnicity),
    options: ethnicityOptions,
    rules: { required: true },
    hidden: !showDemographics,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'formSubheading',
    name: 'contactInformation',
    hidden: !showContactInformation,
    children: intl.formatMessage(messages.contactInformation),
    GridProps: { xs: 12 },
  },
  {
    variant: 'phone',
    name: 'phoneNumber',
    hidden: !showContactInformation,
    label: intl.formatMessage(messages.phoneNumber),
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'phoneType',
    hidden: !showContactInformation,
    label: intl.formatMessage(messages.phoneType),
    options: phoneTypeOptions,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'email',
    name: 'emailAddress',
    hidden: !showContactInformation,
    label: intl.formatMessage(messages.emailAddress),
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'emailType',
    hidden: !showContactInformation,
    label: intl.formatMessage(messages.emailType),
    options: emailTypeOptions,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'address',
    label: intl.formatMessage(messages.address),
    rules: { required: true },
    hidden: !showMailingAddress,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'city',
    label: intl.formatMessage(messages.city),
    rules: { required: true },
    hidden: !showMailingAddress,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'state',
    label: intl.formatMessage(messages.state),
    options: stateOptions,
    disabled: isStatesLoading,
    rules: { required: true },
    hidden: !showMailingAddress,
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'zipCode',
    name: 'zipCode',
    label: intl.formatMessage(messages.zipCode),
    rules: { required: true },
    hidden: !showMailingAddress,
    GridProps: { xs: 12, sm: 6 },
  },
];
